import { PageHeader, DataTable, ActionToolbar } from '../components/AdminPageUI';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Checkbox, Chip, Container, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, Grid, IconButton, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../utils/Api';
import {
  PERMISSION_KEYS,
  PERMISSION_LABELS,
  buildPermissions,
  hasPermission,
  normalizePermissions,
} from '../utils/permissions';

const createBlankForm = () => ({
  name: '',
  email: '',
  phone: '',
  password: '',
  permissions: buildPermissions(false),
});

export default function AdminManagementPage() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [form, setForm] = useState(createBlankForm);

  const canManageAdmins = hasPermission('canManageAdmins');

  const selectedPermissionCount = useMemo(
    () => PERMISSION_KEYS.filter((key) => form.permissions[key]).length,
    [form.permissions]
  );

  const fetchAdmins = useCallback(async () => {
    if (!canManageAdmins) return;

    try {
      setLoading(true);
      const res = await api.get('/auth/admins');
      setAdmins(Array.isArray(res.data?.admins) ? res.data.admins : []);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to load admins');
    } finally {
      setLoading(false);
    }
  }, [canManageAdmins]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const openCreateDialog = () => {
    setEditingAdmin(null);
    setForm(createBlankForm());
    setDialogOpen(true);
  };

  const openEditDialog = (admin) => {
    setEditingAdmin(admin);
    setForm({
      name: admin.name || '',
      email: admin.email || '',
      phone: admin.phone || '',
      password: '',
      permissions: normalizePermissions(admin.permissions),
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditingAdmin(null);
    setForm(createBlankForm());
  };

  const handleChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const togglePermission = (permissionName) => (event) => {
    setForm((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permissionName]: event.target.checked,
      },
    }));
  };

  const setAllPermissions = (value) => {
    setForm((prev) => ({
      ...prev,
      permissions: buildPermissions(value),
    }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Name and email are required');
      return;
    }

    if (!editingAdmin && !form.password.trim()) {
      toast.error('Password is required for a new admin');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        permissions: normalizePermissions(form.permissions),
      };

      if (form.password.trim()) {
        payload.password = form.password.trim();
      }

      if (editingAdmin) {
        await api.put(`/auth/admins/${editingAdmin.id || editingAdmin._id}`, payload);
        toast.success('Admin updated');
      } else {
        await api.post('/auth/admins', payload);
        toast.success('Admin created');
      }

      closeDialog();
      fetchAdmins();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to save admin');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (admin) => {
    const adminId = admin.id || admin._id;
    const confirmed = window.confirm(`Delete admin account for ${admin.name}?`);
    if (!confirmed) return;

    try {
      await api.delete(`/auth/admins/${adminId}`);
      toast.success('Admin deleted');
      fetchAdmins();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to delete admin');
    }
  };

  if (!canManageAdmins) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">You are not allowed to perform this action</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <PageHeader title={<>Admin Management</>} description={<>Create separate admin logins and control exactly what each account can do.</>} actions={<>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
                Add Admin
              </Button>
      </>}></PageHeader>

      <Paper className="workspace-section" elevation={0} sx={{ border: 1, borderColor: 'divider', overflow: 'hidden' }}>
        <DataTable>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Admin</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Permissions</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography color="text.secondary">Loading admins...</Typography>
                  </TableCell>
                </TableRow>
              ) : admins.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography color="text.secondary">No additional admin accounts found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                admins.map((admin) => {
                  const permissionCount = PERMISSION_KEYS.filter(
                    (key) => admin.permissions?.[key]
                  ).length;

                  return (
                    <TableRow key={admin.id || admin._id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography fontWeight={700}>{admin.name}</Typography>
                          {admin.isPrimaryAdmin && (
                            <Chip size="small" color="success" label="Primary admin" />
                          )}
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {admin.email}
                        </Typography>
                      </TableCell>
                      <TableCell>{admin.phone || 'No phone'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={permissionCount === PERMISSION_KEYS.length ? 'success' : 'primary'}
                          label={`${permissionCount}/${PERMISSION_KEYS.length} permissions`}
                        />
                      </TableCell>
                      <TableCell>
                        {admin.createdAt ? new Date(admin.createdAt).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit admin">
                          <IconButton color="primary" onClick={() => openEditDialog(admin)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete admin">
                          <span>
                            <IconButton
                              color="error"
                              onClick={() => handleDelete(admin)}
                              disabled={admin.isPrimaryAdmin}
                            >
                            <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </DataTable>
      </Paper>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>{editingAdmin ? 'Edit Admin' : 'Add Admin'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 6 }}   >
              <TextField
                fullWidth
                label="Name"
                value={form.name}
                onChange={handleChange('name')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}   >
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={form.email}
                onChange={handleChange('email')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}   >
              <TextField
                fullWidth
                label="Phone"
                value={form.phone}
                onChange={handleChange('phone')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}   >
              <TextField
                fullWidth
                label={editingAdmin ? 'New password (optional)' : 'Password'}
                type="password"
                value={form.password}
                onChange={handleChange('password')}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6" fontWeight={800}>
                Permissions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedPermissionCount} selected
              </Typography>
            </Box>
            <ActionToolbar direction="row" spacing={1}>
              <Button size="small" onClick={() => setAllPermissions(true)}>
                Select All
              </Button>
              <Button size="small" color="inherit" onClick={() => setAllPermissions(false)}>
                Clear
              </Button>
            </ActionToolbar>
          </Stack>

          <Grid container spacing={1}>
            {PERMISSION_KEYS.map((permissionName) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}     key={permissionName}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(form.permissions[permissionName])}
                      onChange={togglePermission(permissionName)}
                    />
                  }
                  label={PERMISSION_LABELS[permissionName] || permissionName}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : 'Save Admin'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
